import { useState } from 'react';
import { MoreVertical, Flag, Ban, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';

const REPORT_REASONS = [
  { value: 'inappropriate', label: 'Inappropriate behavior' },
  { value: 'spam', label: 'Spam' },
  { value: 'fake_profile', label: 'Fake profile' },
  { value: 'other', label: 'Other' },
];

/**
 * MemberSafetyMenu — three-dot dropdown attached to any member profile card.
 *
 * Props:
 *   userId   — id of the member shown
 *   userName — display name (e.g. "Maya")
 *   onBlocked — optional callback fired after a successful block (lets parent hide the card)
 *   size — "sm" | "md" (default "md") — controls trigger button size
 *
 * Self-contained: handles its own report & block confirmation dialogs.
 */
export default function MemberSafetyMenu({ userId, userName = 'this member', onBlocked, size = 'md' }) {
  const [reportOpen, setReportOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const triggerCls = size === 'sm'
    ? 'h-7 w-7 text-text-mid'
    : 'h-8 w-8 text-text-mid';

  const submitReport = async () => {
    if (!reason) { toast.error('Please choose a reason'); return; }
    setSubmitting(true);
    try {
      await api.post('/safety/report', { reported_user_id: userId, reason, details });
      toast.success('Thank you — our team will review this within 24 hours');
      setReportOpen(false);
      setReason('');
      setDetails('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const submitBlock = async () => {
    setSubmitting(true);
    try {
      await api.post('/safety/block', { user_id: userId });
      toast.success('This member will no longer be able to see your profile or message you');
      setBlockOpen(false);
      onBlocked?.(userId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to block');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={triggerCls}
            data-testid={`member-safety-menu-${userId}`}
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical size={size === 'sm' ? 14 : 16} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuItem
            onClick={() => setReportOpen(true)}
            data-testid={`report-member-${userId}`}
          >
            <Flag size={14} className="mr-2" /> Report
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setBlockOpen(true)}
            className="text-red-600 focus:text-red-700"
            data-testid={`block-member-${userId}`}
          >
            <Ban size={14} className="mr-2" /> Block
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Report Dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-md" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Report {userName}</DialogTitle>
            <DialogDescription>
              Your report is anonymous. Our safety team reviews every report.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              {REPORT_REASONS.map((r) => (
                <label key={r.value} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="report-reason"
                    value={r.value}
                    checked={reason === r.value}
                    onChange={(e) => setReason(e.target.value)}
                    data-testid={`report-reason-${r.value}`}
                  />
                  {r.label}
                </label>
              ))}
            </div>
            <div>
              <Textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Optional: anything we should know? (private to admin)"
                rows={3}
                maxLength={1000}
                className="rounded-xl"
                data-testid="report-details-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReportOpen(false)}>Cancel</Button>
            <Button
              onClick={submitReport}
              disabled={submitting || !reason}
              className="bg-teal hover:bg-teal-deep text-white rounded-full"
              data-testid="submit-report-btn"
            >
              Submit Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block Dialog */}
      <Dialog open={blockOpen} onOpenChange={setBlockOpen}>
        <DialogContent className="max-w-md" data-testid="block-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Block {userName}?</DialogTitle>
            <DialogDescription>
              This member will no longer be able to see your profile or message you. You can unblock
              them anytime in Settings → Privacy → Blocked Members.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 text-sm text-text-mid">
            <ShieldCheck size={16} className="text-teal" />
            Mutually invisible — they won't show up in your matches and you won't show up in theirs.
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBlockOpen(false)}>Cancel</Button>
            <Button
              onClick={submitBlock}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full"
              data-testid="confirm-block-btn"
            >
              Block {userName}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
